import React, { useContext, useMemo } from 'react';
import styles from './MfSelectComposition.module.css';
import { CheckedIcon, CompositionIcon } from 'features/compositions/assets/icons/icons';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { ArrowRightIcon } from 'assets/icons';
import useTheme from 'config/useTheme';
import AuthenticationContext from 'config/context/authenticationContext';
import { useMfImport } from 'features/compositions/context/MfImportContext';

/**
 * Poređenje strukture izvora sa izabranom kompozicijom.
 *
 * Uvoz u postojeću kompoziciju prenosi ISKLJUČIVO polja koja ta kompozicija
 * već ima. Host to odbija pre prvog upisa, ali odbijanje stiže tek posle unosa
 * privatnog ključa — a razlog je vidljiv već ovde, u trenutku izbora.
 *
 * Tri ishoda, i sva tri korisnika zanimaju:
 *   missing  polja izvora kojih kompozicija NEMA → uvoz nije moguć
 *   matched  poklopljena → prenose se
 *   filler   polja kompozicije kojih u izvoru nema → dobijaju nultu vrednost
 *
 * Indeksi se u `filler` ne broje: primarni ključ dodeljuje kontrakt, pa to što
 * ga u izvoru nema nije gubitak nego pravilo.
 */
const compareFields = (source, composition) => {
    if (!composition) return null;

    const targetAll = new Set();
    for (const f of [...(composition.indexes || []), ...(composition.fields || [])]) {
        if (f?.name) targetAll.add(f.name);
    }

    // `table.fields` već nosi i indekse izvora, spojene u koraku 1.
    const sourceNames = (source?.fields || []).map((f) => f?.name).filter(Boolean);
    const inSource = new Set(sourceNames);

    return {
        missing: sourceNames.filter((n) => !targetAll.has(n)),
        matched: sourceNames.filter((n) => targetAll.has(n)),
        filler: (composition.fields || [])
            .map((f) => f?.name)
            .filter((n) => n && !inSource.has(n)),
    };
};

const nameList = (names, limit = 8) => {
    const shown = names.slice(0, limit).join(', ');
    return names.length > limit ? `${shown}, and ${names.length - limit} more` : shown;
};

const MfSelectComp = () => {
    const { dark } = useTheme();
    const { getCompositionPermission } = useContext(AuthenticationContext);
    const { compositionNames, databaseStructure } = useCompositionsContext();
    const { setStep, selectedComposition, setSelectedComposition, setSelectedPermission, table } = useMfImport();

    const diff = useMemo(() => {
        if (!selectedComposition) return null;
        const composition = databaseStructure?.compositions?.find((c) => c.name === selectedComposition);
        return compareFields(table, composition);
    }, [selectedComposition, databaseStructure, table]);

    const blocked = (diff?.missing.length ?? 0) > 0;

    const goToAuth = () => {
        setStep(3);
    }

    const selectComp = (t) => {
        setSelectedComposition(t);
        setSelectedPermission(getCompositionPermission(t));
    }

    return (
        <div className={`${styles.select_composition} ${dark ? styles.dark : ''}`}>
            <div className={styles.compositions_list}>
                {compositionNames?.map((t) => (
                    <button key={t} onClick={() => selectComp(t)} className={selectedComposition === t ? styles.active : ''}>
                        <span><CompositionIcon /> {t}</span> <div><CheckedIcon /></div>
                    </button>
                ))}
            </div>

            {diff && (
                <div className={styles.field_match}>
                    {blocked ? (
                        <p className={styles.field_match_blocked}>
                            <b>{diff.missing.length === 1 ? '1 field' : `${diff.missing.length} fields`} in the source
                            {diff.missing.length === 1 ? ' is' : ' are'} not in '{selectedComposition}'</b>
                            : {nameList(diff.missing)}. An import into an existing composition carries only the fields
                            that composition already has, so these would be dropped without a trace — and rows written
                            to the chain cannot be taken back. Use "Create new composition" instead, or pick one that
                            matches this source.
                        </p>
                    ) : (
                        <p className={styles.field_match_ok}>
                            All {diff.matched.length} source {diff.matched.length === 1 ? 'field' : 'fields'} exist
                            in '{selectedComposition}'. The rows will be appended to it; nothing already there is
                            changed or removed.
                        </p>
                    )}

                    {/* I kad uvoz prolazi, polja kompozicije kojih u izvoru nema
                        dobijaju nultu vrednost svog tipa — koja se posle ne
                        razlikuje od unete. To je isti razlog zbog kog izveštaj
                        o degradaciji postoji, pa se i ovde kaže unapred. */}
                    {!blocked && diff.filler.length > 0 && (
                        <p className={styles.field_match_note}>
                            {diff.filler.length === 1 ? '1 field of' : `${diff.filler.length} fields of`} the
                            composition {diff.filler.length === 1 ? 'is' : 'are'} not in the source
                            ({nameList(diff.filler)}) and will be written as the zero value of their type, which
                            afterwards cannot be told apart from an entered one.
                        </p>
                    )}
                </div>
            )}

            <button disabled={!selectedComposition || blocked} onClick={() => goToAuth()} className={styles.go_to_auth}>Go to authorization <ArrowRightIcon /></button>
        </div>
    )
}

export default MfSelectComp;
